import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  FolderInput,
  MonitorPlay,
  MoveRight,
  Power,
  ShieldOff,
  Tags,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { GatedButton } from '@/components/ui/GatedButton';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { MoveDeviceDialog } from './MoveDeviceDialog';
import { ChangeTagsDialog } from './ChangeTagsDialog';
import { rebootDevice, revokeDevice } from '@/lib/api/endpoints/devices';
import { useAuth } from '@/lib/auth/AuthProvider';
import { queryKeys } from '@/lib/query-keys';
import type { Device, DevicePresence, Permission } from '@/lib/api/types';

/*
  The action row on the device detail page.

  Three kinds of button live here and they are deliberately distinguishable:

  1. Built and permitted: an ordinary button.
  2. Built but not permitted for this account: a GatedButton whose reason names the missing
     permission. Not hidden. A technician who cannot reboot should be able to see that reboot
     exists and learn which permission they would need, rather than wonder whether the console
     is broken. (Hiding it would also be no more secure: docs/07-identity-rbac.md I.3 is
     explicit that the UI is never consulted.)
  3. Not built yet: a GatedButton whose reason names the phase that delivers it. Remote
     Control is Phase 2 and File Transfer is Phase 4, per docs/10-roadmap.md. These are the
     two buttons that would otherwise be the most tempting to wire to a dead handler, so they
     say what they are instead.

  Remote Control does NOT call connectDevice(), even though that function now has a working
  mock. There is no viewer to hand the session to, so calling it would create a pending
  session on the server that nothing will ever answer, and it would leave a session row and an
  audit entry implying a connection happened. A button that says Phase 2 is more honest than a
  button that starts something nobody finishes.

  Reboot and revoke are the two destructive actions and both go through ConfirmDialog with the
  hostname in the copy and the consequence spelled out. The revoke copy in particular states
  the thing people get wrong: revoking does not uninstall anything.
*/

export interface DeviceActionBarProps {
  readonly device: Device;
  readonly presence: DevicePresence | null;
  readonly organizationName: string;
}

export function DeviceActionBar({
  device,
  presence,
  organizationName,
}: DeviceActionBarProps): React.JSX.Element {
  const { can } = useAuth();
  const queryClient = useQueryClient();

  const [rebootOpen, setRebootOpen] = useState(false);
  const [revokeOpen, setRevokeOpen] = useState(false);
  const [moveOpen, setMoveOpen] = useState(false);
  const [tagsOpen, setTagsOpen] = useState(false);
  const [notifyUser, setNotifyUser] = useState(true);

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['devices'] });
    void queryClient.invalidateQueries({ queryKey: queryKeys.auditEvents('') });
    void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard });
  };

  const reboot = useMutation({
    mutationFn: (reason: string) => rebootDevice(device.id, { reason, notifyUser }),
    onSuccess: invalidate,
  });

  const revoke = useMutation({
    mutationFn: (reason: string) => revokeDevice(device.id, { reason }),
    onSuccess: invalidate,
  });

  const online = presence !== null && presence.state !== 'offline';
  const revoked = device.status === 'revoked';
  const loggedInUser = presence?.loggedInUser ?? null;

  /** Reason text for an action that exists but this account may not use. */
  function missing(permission: Permission, what: string): string {
    return `${what} needs the ${permission} permission, and none of your role assignments grant it at this scope. Ask an administrator for a role that covers this device.`;
  }

  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        {/* Phase 2. See the note above on why this does not call connectDevice(). */}
        <GatedButton
          variant="primary"
          badge="Phase 2"
          reason="Remote control arrives in Phase 2, which delivers the WebRTC transport and the viewer. The API contract for starting a session already exists; there is nothing yet to display the screen in."
        >
          <MonitorPlay className="size-4" aria-hidden="true" />
          Remote Control
        </GatedButton>

        {/* Phase 4. */}
        <GatedButton
          badge="Phase 4"
          reason="File transfer arrives in Phase 4, alongside the transfer audit records and the per-organization policy that can switch it off."
        >
          <FolderInput className="size-4" aria-hidden="true" />
          File Transfer
        </GatedButton>

        {can('device.reboot') ? (
          <Button
            variant="secondary"
            disabled={revoked || !online}
            title={
              revoked
                ? 'A revoked device holds no usable credential, so no command can reach it.'
                : online
                  ? undefined
                  : 'The device is offline. A reboot command would sit in a queue rather than run.'
            }
            onClick={() => {
              setRebootOpen(true);
            }}
          >
            <Power className="size-4" aria-hidden="true" />
            Restart
          </Button>
        ) : (
          <GatedButton reason={missing('device.reboot', 'Restarting a device')}>
            <Power className="size-4" aria-hidden="true" />
            Restart
          </GatedButton>
        )}

        {can('device.manage') ? (
          <>
            <Button
              variant="secondary"
              onClick={() => {
                setMoveOpen(true);
              }}
            >
              <MoveRight className="size-4" aria-hidden="true" />
              Move Device
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                setTagsOpen(true);
              }}
            >
              <Tags className="size-4" aria-hidden="true" />
              Change Tags
            </Button>
            <Button
              variant="danger"
              disabled={revoked}
              title={revoked ? 'This device is already revoked.' : undefined}
              onClick={() => {
                setRevokeOpen(true);
              }}
            >
              <ShieldOff className="size-4" aria-hidden="true" />
              Revoke
            </Button>
          </>
        ) : (
          <>
            <GatedButton reason={missing('device.manage', 'Moving a device')}>
              <MoveRight className="size-4" aria-hidden="true" />
              Move Device
            </GatedButton>
            <GatedButton reason={missing('device.manage', 'Changing tags')}>
              <Tags className="size-4" aria-hidden="true" />
              Change Tags
            </GatedButton>
            <GatedButton reason={missing('device.manage', 'Revoking a device')}>
              <ShieldOff className="size-4" aria-hidden="true" />
              Revoke
            </GatedButton>
          </>
        )}
      </div>

      <ConfirmDialog
        open={rebootOpen}
        onOpenChange={setRebootOpen}
        title={`Restart ${device.hostname}?`}
        summary={`${device.hostname} at ${organizationName}. This sends a reboot command to the agent.`}
        consequence={
          <>
            <p>
              {device.hostname} will restart. Anything unsaved on it is lost, and it will be
              unreachable for the length of its boot.
            </p>
            {loggedInUser === null ? (
              <p className="mt-1">
                No interactive user is signed in at the moment, so nobody is at the keyboard to
                lose work. That can change between now and the command arriving.
              </p>
            ) : (
              <p className="mt-1">
                <span className="font-mono">{loggedInUser}</span> is signed in at this machine
                right now.
              </p>
            )}
          </>
        }
        confirmLabel="Restart device"
        tone="danger"
        requireReason
        reasonLabel="Why are you restarting it"
        reasonHint="Recorded in the audit log against your name, and shown to the customer if they ask."
        onConfirm={(reason) => reboot.mutateAsync(reason)}
      >
        <label className="flex items-start gap-2 text-sm text-fg">
          <input
            type="checkbox"
            checked={notifyUser}
            onChange={(event) => {
              setNotifyUser(event.target.checked);
            }}
            className="mt-0.5 size-4 accent-[var(--color-accent)]"
          />
          <span>
            Warn the signed-in user first and give them a moment
            <span className="block text-xs text-fg-subtle">
              Clearing this restarts immediately. On a machine someone is working at, that is
              the difference between an inconvenience and lost work.
            </span>
          </span>
        </label>
      </ConfirmDialog>

      <ConfirmDialog
        open={revokeOpen}
        onOpenChange={setRevokeOpen}
        title={`Revoke ${device.hostname}?`}
        summary={`${device.hostname} at ${organizationName}. This permanently invalidates the device credential.`}
        consequence={
          <>
            <p>
              The Ed25519 credential for {device.hostname} is invalidated immediately and its
              gateway connection is force-closed, whether or not the machine is online to hear
              about it. Nobody can connect to it again through JIT.
            </p>
            <p className="mt-1">
              <strong>The agent is not uninstalled.</strong> The service and its binaries stay
              on disk exactly where they are, and the Windows Event Log entries it has written
              stay too. What changes is that it can no longer authenticate to anything. To
              remove the software, uninstall it on the machine.
            </p>
            <p className="mt-1">
              Re-enrolling later means running the installer again with a fresh enrollment
              token. Revoking cannot be undone from here.
            </p>
          </>
        }
        confirmLabel="Revoke this device"
        tone="danger"
        requireReason
        reasonLabel="Why are you revoking it"
        reasonHint="Stored on the device record and in the audit log. Write it for whoever reads this in six months."
        onConfirm={(reason) => revoke.mutateAsync(reason)}
      />

      <MoveDeviceDialog
        open={moveOpen}
        onOpenChange={setMoveOpen}
        device={device}
        currentOrganizationName={organizationName}
      />

      <ChangeTagsDialog open={tagsOpen} onOpenChange={setTagsOpen} device={device} />
    </>
  );
}
